import Configuration from '@/api/Configuration';
import {
  toApiRetailerCreateRequest,
  toApiRetailerUpdateRequest,
  toRetailer,
} from '@/api/mappers/mapRetailer';
import {
  RetailersApi as ApiRetailersApi,
  CreateRetailerRequest,
  ListRetailersRequest,
  UpdateRetailerRequest,
} from '@/gen/api/apis/RetailersApi';
import {
  Retailer,
  RetailerAssociation,
  RetailerCreate,
  Retailers,
  RetailerUpdate,
} from '@/models/Retailer';

export interface RetailerFilters {
  supplierId?: string;
  retailerId?: string[];
  supplierRetailerStatus?: 'ACTIVE' | 'INACTIVE';
  productListId?: string;
  exclude?: boolean;
}

interface RetailersApi {
  listRetailers(
    offset?: number,
    limit?: number,
    include?: RetailerAssociation[],
  ): Promise<Retailers>;
  findRetailers(
    query?: string,
    offset?: number,
    limit?: number,
    include?: RetailerAssociation[],
    filters?: RetailerFilters,
  ): Promise<Retailers>;
  createRetailer(retailer: RetailerCreate): Promise<void>;
  getRetailer(id: string): Promise<Retailer | undefined>;
  getRetailerWithSupplierContext(id: string, supplierId: string): Promise<Retailer | undefined>;
  updateRetailer(id: string, retailer: RetailerUpdate): Promise<void>;
  deleteRetailer(id: string): Promise<void>;
  listRetailersBySupplier(supplierId: string, offset?: number, limit?: number): Promise<Retailers>;
  findRetailersBySupplier(
    supplierId: string,
    query?: string,
    offset?: number,
    limit?: number,
  ): Promise<Retailers>;
}

export class RetailersApiImpl implements RetailersApi {
  private retailersApi: ApiRetailersApi;

  constructor(configuration: Configuration) {
    this.retailersApi = new ApiRetailersApi(configuration);
  }

  async listRetailers(offset = 0, limit = 10, include?: RetailerAssociation[]): Promise<Retailers> {
    try {
      const params: ListRetailersRequest = {
        offset,
        limit,
        include,
      };

      const response = await this.retailersApi.listRetailers(params);

      return {
        totalCount: response.totalCount,
        estimatedTotalCount: response.estimatedTotalCount,
        items: response.items.map(toRetailer),
      };
    } catch (error) {
      console.error('Error listing retailers:', error);
      throw error;
    }
  }

  async findRetailers(
    query?: string,
    offset = 0,
    limit = 10,
    include?: RetailerAssociation[],
    filters?: RetailerFilters,
  ): Promise<Retailers> {
    try {
      const params: ListRetailersRequest = {
        ...filters,
        query,
        offset,
        limit,
        include,
      };

      const response = await this.retailersApi.listRetailers(params);
      return {
        totalCount: response.totalCount,
        estimatedTotalCount: response.estimatedTotalCount,
        items: response.items.map(toRetailer),
      };
    } catch (error) {
      console.error('Error finding retailers:', error);
      throw error;
    }
  }

  async createRetailer(retailer: RetailerCreate): Promise<void> {
    try {
      const params: CreateRetailerRequest = {
        retailerCreateRequest: toApiRetailerCreateRequest(retailer),
      };

      await this.retailersApi.createRetailer(params);
    } catch (error) {
      console.error('Error creating retailer:', error);
      throw error;
    }
  }

  async getRetailer(id: string): Promise<Retailer | undefined> {
    try {
      const response = await this.retailersApi.getRetailer({ id });
      return toRetailer(response);
    } catch (error) {
      console.error('Error getting retailer:', error);
      throw error;
    }
  }

  async getRetailerWithSupplierContext(
    id: string,
    supplierId: string,
  ): Promise<Retailer | undefined> {
    try {
      const response = await this.retailersApi.getRetailer({
        id,
        supplierId,
      });
      return toRetailer(response);
    } catch (error) {
      console.error('Error getting retailer:', error);
      throw error;
    }
  }

  async updateRetailer(id: string, retailer: RetailerUpdate): Promise<void> {
    try {
      const params: UpdateRetailerRequest = {
        id,
        retailerUpdateRequest: toApiRetailerUpdateRequest(retailer),
      };

      await this.retailersApi.updateRetailer(params);
    } catch (error) {
      console.error('Error updating retailer:', error);
      throw error;
    }
  }

  async deleteRetailer(id: string): Promise<void> {
    try {
      await this.retailersApi.deleteRetailer({ id });
    } catch (error) {
      console.error('Error deleting retailer:', error);
      throw error;
    }
  }

  async listRetailersBySupplier(supplierId: string, offset = 0, limit = 10): Promise<Retailers> {
    try {
      const params: ListRetailersRequest = {
        supplierId,
        offset,
        limit,
      };

      const response = await this.retailersApi.listRetailers(params);
      return {
        totalCount: response.totalCount,
        estimatedTotalCount: response.estimatedTotalCount,
        items: response.items.map(toRetailer),
      };
    } catch (error) {
      console.error('Error listing retailers by supplier:', error);
      throw error;
    }
  }

  async findRetailersBySupplier(
    supplierId: string,
    query?: string,
    offset = 0,
    limit = 10,
  ): Promise<Retailers> {
    try {
      const params: ListRetailersRequest = {
        supplierId,
        query,
        offset,
        limit,
      };

      const response = await this.retailersApi.listRetailers(params);
      return {
        totalCount: response.totalCount,
        estimatedTotalCount: response.estimatedTotalCount,
        items: response.items.map(toRetailer),
      };
    } catch (error) {
      console.error('Error finding retailers by supplier:', error);
      throw error;
    }
  }
}

export default RetailersApi;
